/**
 * Past calls in the left drawer, laid out like the session list of a chat
 * app: single-line titles grouped by when they happened, newest first, with
 * the next page fetched as the bottom of the list scrolls into view. Each
 * row has a delete button that asks for confirmation in place; on touch
 * screens, where it would always show, it sits in a "⋯" menu instead.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CallPlace, CallSummary, callCountry, deleteCall, fmtCallDateTime, listCalls } from './callsApi';
import { zoneForCountry } from './timeFormat';
import './Calls.css';

const DAY_MS = 24 * 60 * 60 * 1000;

/** The calendar day of `d` in a zone (the viewer's when undefined), as midnight UTC, so day differences are whole numbers. */
function calendarDay(d: Date, timeZone: string | undefined): number {
  const ymd = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  return Date.parse(`${ymd}T00:00:00Z`);
}

/**
 * The heading a call is listed under, relative to `now`. Days are counted in
 * the zone the call's times are shown in: Japan time for calls to Japan, the
 * viewer's own zone otherwise (see timeFormat.ts).
 */
export function sectionLabel(call: CallPlace & { initiatedAt: string | null }, now: Date = new Date()): string {
  if (!call.initiatedAt) return 'Earlier';
  const when = new Date(call.initiatedAt);
  if (isNaN(when.getTime())) return 'Earlier';
  const timeZone = zoneForCountry(callCountry(call));
  const daysAgo = Math.round((calendarDay(now, timeZone) - calendarDay(when, timeZone)) / DAY_MS);
  if (daysAgo <= 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo <= 7) return 'Previous 7 days';
  if (daysAgo <= 30) return 'Previous 30 days';
  const sameYear = new Date(calendarDay(when, timeZone)).getUTCFullYear() === new Date(calendarDay(now, timeZone)).getUTCFullYear();
  return when.toLocaleString(undefined, { timeZone, month: 'long', ...(sameYear ? {} : { year: 'numeric' }) });
}

interface Section {
  label: string;
  calls: CallSummary[];
}

/** Calls are already newest first, so each heading appears once in order. */
export function groupBySection(calls: CallSummary[], now: Date = new Date()): Section[] {
  const sections: Section[] = [];
  for (const call of calls) {
    const label = sectionLabel(call, now);
    const last = sections[sections.length - 1];
    if (last && last.label === label) last.calls.push(call);
    else sections.push({ label, calls: [call] });
  }
  return sections;
}

/** A page appended to the list; a call already listed is not added twice. */
export function appendCalls(prev: CallSummary[], page: CallSummary[]): CallSummary[] {
  const listed = new Set(prev.map((c) => c.callId));
  return [...prev, ...page.filter((c) => !listed.has(c.callId))];
}

function rowTooltip(call: CallSummary): string {
  const parts = [call.toNumber, call.initiatedAt && fmtCallDateTime(call.initiatedAt, call)];
  return parts.filter(Boolean).join(' · ');
}

interface CallHistoryProps {
  /** Whether the drawer is showing; the list reloads each time it opens. */
  open: boolean;
  /** Called when a call is picked, so the drawer can close. */
  onNavigate: () => void;
}

function CallHistory({ open, onNavigate }: CallHistoryProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Every fresh (first-page) load starts a new listing; a page that comes back
  // for an older listing is dropped, so reopening the drawer while a next page
  // is still in flight can't append that page to the fresh list.
  const listing = useRef(0);
  const pending = useRef(new Set<string>());
  // The row asking "Delete this call?", and how that delete is going.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // The row whose "⋯" menu is open (touch screens).
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // A tap anywhere else, or Escape, closes the menu.
  useEffect(() => {
    if (!menuFor) return;
    const onPointerDown = (e: PointerEvent | MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuFor(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuFor(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuFor]);

  const loadPage = useCallback(async (cursor: string | null) => {
    if (cursor === null) listing.current += 1;
    const generation = listing.current;
    const key = `${generation}:${cursor ?? ''}`;
    if (pending.current.has(key)) return;
    pending.current.add(key);
    setLoading(true);
    setError('');
    try {
      const page = await listCalls(cursor);
      if (generation !== listing.current) return;
      setCalls((prev) => (cursor ? appendCalls(prev, page.calls) : page.calls));
      setNextCursor(page.nextCursor);
      setLoaded(true);
    } catch (e) {
      if (generation === listing.current) setError(e instanceof Error ? e.message : 'Could not load calls');
    } finally {
      pending.current.delete(key);
      if (pending.current.size === 0) setLoading(false);
    }
  }, []);

  // Fresh first page whenever the drawer opens (a call may have just ended).
  useEffect(() => {
    if (open) loadPage(null);
  }, [open, loadPage]);

  // Lazy loading: fetch the next page once the sentinel below the list scrolls
  // into view. The drawer is off-screen while closed, so nothing loads then.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !nextCursor || loading || error) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadPage(nextCursor);
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [nextCursor, loading, error, loadPage]);

  const activeCallId = location.pathname.startsWith('/calls/')
    ? decodeURIComponent(location.pathname.slice('/calls/'.length))
    : null;

  const openCall = (callId: string) => {
    onNavigate();
    navigate(`/calls/${encodeURIComponent(callId)}`);
  };

  const askToDelete = (callId: string | null) => {
    setConfirming(callId);
    setDeleteError('');
  };

  const confirmDelete = async (callId: string) => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteCall(callId);
      setCalls((prev) => prev.filter((c) => c.callId !== callId));
      setConfirming(null);
      if (callId === activeCallId) navigate('/');
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Could not delete the call');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <nav className="call-history" aria-label="Call history">
      {groupBySection(calls).map((section) => (
        <div className="call-history-section" key={section.label}>
          <div className="call-history-heading">{section.label}</div>
          {section.calls.map((call) =>
            call.callId === confirming ? (
              <div className="call-history-confirm" key={call.callId} role="group" aria-label={`Delete ${call.title}`}>
                <div className="call-history-confirm-title">Delete “{call.title}”?</div>
                <div className="call-history-confirm-note">
                  The call and its transcript will be permanently deleted. This cannot be recovered.
                </div>
                {deleteError && <div className="call-history-confirm-note error">{deleteError}</div>}
                <div className="call-history-confirm-actions">
                  <button
                    type="button"
                    className="call-history-confirm-delete"
                    disabled={deleting}
                    onClick={() => confirmDelete(call.callId)}
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                  <button type="button" className="call-history-confirm-cancel" disabled={deleting} onClick={() => askToDelete(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className={`call-history-row${call.callId === activeCallId ? ' active' : ''}`} key={call.callId}>
                <button
                  type="button"
                  className={`call-history-item${call.callId === activeCallId ? ' active' : ''}`}
                  title={rowTooltip(call)}
                  aria-current={call.callId === activeCallId ? 'page' : undefined}
                  onClick={() => openCall(call.callId)}
                >
                  {call.title}
                </button>
                <button
                  type="button"
                  className="call-history-delete"
                  aria-label={`Delete ${call.title}`}
                  title="Delete call"
                  disabled={deleting}
                  onClick={() => askToDelete(call.callId)}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path
                      fill="currentColor"
                      d="M9 3h6l1 1h4v2H4V4h4l1-1zm-3 5h12l-1 12a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 8zm4 2v9h2v-9h-2zm4 0v9h2v-9h-2z"
                    />
                  </svg>
                </button>
                <div className="call-history-more-wrap" ref={call.callId === menuFor ? menuRef : undefined}>
                  <button
                    type="button"
                    className="call-history-more"
                    aria-label={`More actions for ${call.title}`}
                    aria-haspopup="menu"
                    aria-expanded={call.callId === menuFor}
                    disabled={deleting}
                    onClick={() => setMenuFor(call.callId === menuFor ? null : call.callId)}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                      <circle cx="5" cy="12" r="2" fill="currentColor" />
                      <circle cx="12" cy="12" r="2" fill="currentColor" />
                      <circle cx="19" cy="12" r="2" fill="currentColor" />
                    </svg>
                  </button>
                  {call.callId === menuFor && (
                    <div className="call-history-menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        className="call-history-menu-item danger"
                        onClick={() => {
                          setMenuFor(null);
                          askToDelete(call.callId);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      ))}
      {loaded && calls.length === 0 && !error && (
        <div className="call-history-note">No calls yet</div>
      )}
      {loading && <div className="call-history-note">Loading…</div>}
      {error && (
        <div className="call-history-note error">
          {error}{' '}
          <button type="button" className="call-history-retry" onClick={() => loadPage(calls.length ? nextCursor : null)}>
            Retry
          </button>
        </div>
      )}
      <div ref={sentinelRef} className="call-history-sentinel" aria-hidden="true" />
    </nav>
  );
}

export default CallHistory;
